import type { Permissions } from '../../features/auth/permissions';

export type NavGroup = 'workforce' | 'operations' | 'admin';

export const NAV_GROUP_LABEL: Record<NavGroup, string> = {
  workforce: 'Workforce',
  operations: 'Operations',
  admin: 'Admin',
};

/** Which live count (if any) a nav item shows as a mono badge. Resolved in
 * `AppShell` behind the same permission as the item itself. */
export type NavBadgeKey = 'pendingLeave' | 'unreadNotifications';

export interface NavItem {
  label: string;
  /** Re-design T11 — sidebar section. Grouping never changes visibility. */
  group: NavGroup;
  badge?: NavBadgeKey;
  path: string;
  /** Material Symbols Outlined ligature name (Stage 5 UI Shell —
   * `00a-component-library/code.html` §11's tenant sidebar). */
  icon: string;
  /** shown only if the caller holds at least one of these permissions.
   * `[]` = always shown (Dashboard). */
  anyOf: string[];
  /** extra predicate — e.g. hide a module the caller only holds at `self`
   * scope, which is a "my record" view, not the admin module. */
  when?: (p: Permissions) => boolean;
}

const notSelfScope =
  (code: string) =>
  (p: Permissions): boolean => {
    const scope = p.scopeOf(code);
    return scope === 'tenant' || scope === 'branch' || scope === 'tenant_readonly';
  };

/**
 * UX Specification §3 — role-scoped left navigation. A user never sees a nav
 * item for a module they hold no permission in (not a greyed-out one that
 * implies access exists). The mapping is permission-driven, not
 * role-driven, so it stays correct if the RBAC matrix changes.
 *
 * Platform roles (PSA/PCA/SUP) get an entirely separate console
 * (Architecture §5.3) — this list is the tenant portal only.
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', group: 'workforce', path: '/', icon: 'grid_view', anyOf: [] },
  {
    label: 'Employees', group: 'workforce',
    path: '/employees',
    icon: 'badge',
    anyOf: ['employee.view'],
    // an EMP holds employee.view at `self` — that's a "my profile" view,
    // not the admin Employees module (UX §3 lists only TCA/HRM/BMS).
    when: notSelfScope('employee.view'),
  },
  { label: 'Attendance', group: 'workforce', path: '/attendance', icon: 'schedule', anyOf: ['attendance.view'] },
  {
    label: 'Leave', group: 'workforce', badge: 'pendingLeave',
    path: '/leave',
    icon: 'event_available',
    anyOf: ['leave.view', 'leave.approve'],
  },
  {
    label: 'Approvals', group: 'workforce',
    path: '/approvals',
    icon: 'task_alt',
    // overtime requests (anyone who may request/view them) and the approval inbox
    anyOf: ['overtime.request', 'overtime.view', 'overtime.approve', 'leave.approve'],
  },
  // The header bell shows the latest at a glance; this is the full list, kept low in the menu. Every signed-in
  // tenant user has their own inbox (`GET /notifications/inbox` needs no permission).
  { label: 'Notifications', group: 'workforce', badge: 'unreadNotifications', path: '/notifications', icon: 'notifications', anyOf: [] },
  { label: 'Devices', group: 'operations', path: '/devices', icon: 'fingerprint', anyOf: ['device.view'] },
  { label: 'Reports', group: 'operations', path: '/reports', icon: 'assessment', anyOf: ['report.view'] },
  { label: 'Payroll', group: 'operations', path: '/payroll', icon: 'payments', anyOf: ['payroll.view'] },
  { label: 'Visitors', group: 'operations', path: '/visitors', icon: 'badge', anyOf: ['access.view'] },
  { label: 'Messages', group: 'operations', path: '/messages', icon: 'mail', anyOf: ['message.view'] },
  { label: 'Settings', group: 'admin', path: '/settings', icon: 'settings', anyOf: ['tenant.edit'] },
];

export function visibleNav(permissions: Permissions): NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      (item.anyOf.length === 0 || permissions.canAny(item.anyOf)) &&
      (!item.when || item.when(permissions)),
  );
}

/** Visible items bucketed by group, in group order; empty groups are dropped. */
export function groupedNav(items: NavItem[]): Array<{ group: NavGroup; label: string; items: NavItem[] }> {
  return (['workforce', 'operations', 'admin'] as NavGroup[])
    .map((group) => ({ group, label: NAV_GROUP_LABEL[group], items: items.filter((i) => i.group === group) }))
    .filter((g) => g.items.length > 0);
}

/** DOM id of the hidden text that describes an item's count badge (`aria-describedby`). */
export function navBadgeId(item: NavItem) {
  return `nav-badge-${item.badge}`;
}
